import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { API_BASE_URL } from '@vsolv/dev-kit/ngx';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { ConnectivityService } from '../services';

/**
 * Watches our own api calls so we can tell "the network is up but we can't reach the api" apart from
 * "everything is fine". Third party requests are ignored - an adblocked or cors-rejected script says
 * nothing about our connectivity.
 */
@Injectable()
export class ConnectivityInterceptor implements HttpInterceptor {
  constructor(
    private connectivitySvc: ConnectivityService,
    @Optional() @Inject(API_BASE_URL) private baseUrl: string | null
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(req)) return next.handle(req);

    return next.handle(req).pipe(
      tap(() => this.connectivitySvc.reportApiReachable()),
      catchError((error: unknown) => {
        // status 0 means the request never got an answer - no dns, no route, or the request was aborted.
        if (error instanceof HttpErrorResponse) {
          if (error.status === 0) this.connectivitySvc.reportApiUnreachable();
          else this.connectivitySvc.reportApiReachable();
        }

        return throwError(() => error);
      })
    );
  }

  private isApiRequest(req: HttpRequest<unknown>) {
    // Runs either before or after the ApiInterceptor rewrites the url, so both shapes have to match.
    return (
      req.url.startsWith('/api') || req.url.startsWith('/v1') || (!!this.baseUrl && req.url.startsWith(this.baseUrl))
    );
  }
}
